import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, RefreshCw, Mail, Check } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { SECTEURS } from "../data/projects";
import { supabase, isSupabaseReady } from "../lib/supabaseClient";
import Seo from "../components/Seo";

const questions = [
  {
    q: "Avez-vous un site internet aujourd'hui ?",
    options: [
      { label: "Oui, récent et soigné", points: 3 },
      { label: "Oui, mais daté ou basique", points: 2 },
      { label: "Juste une page Facebook / Instagram", points: 1 },
      { label: "Aucun site", points: 0 },
    ],
  },
  {
    q: "Quand on cherche votre activité + votre ville sur Google, apparaissez-vous ?",
    options: [
      { label: "En haut des résultats", points: 3 },
      { label: "Quelque part sur la 1ère page", points: 2 },
      { label: "Difficile à trouver", points: 1 },
      { label: "Introuvable", points: 0 },
    ],
  },
  {
    q: "Votre fiche Google (Maps) est-elle complète ? (photos, horaires, avis)",
    options: [
      { label: "Complète et à jour", points: 3 },
      { label: "Partiellement remplie", points: 2 },
      { label: "Créée mais quasi vide", points: 1 },
      { label: "Pas de fiche du tout", points: 0 },
    ],
  },
  {
    q: "En 5 secondes sur votre page, comprend-on ce que vous proposez ?",
    options: [
      { label: "Immédiatement, avec du style", points: 3 },
      { label: "Oui, mais sans relief", points: 2 },
      { label: "Pas vraiment clair", points: 1 },
      { label: "On ne comprend pas", points: 0 },
    ],
  },
  {
    q: "Vos photos en ligne donnent-elles envie ?",
    options: [
      { label: "Photos pro et cohérentes", points: 3 },
      { label: "Correctes, sans plus", points: 2 },
      { label: "Quelques photos moyennes", points: 1 },
      { label: "Peu ou pas de photos", points: 0 },
    ],
  },
  {
    q: "Sur mobile, peut-on vous appeler ou vous localiser en un clic ?",
    options: [
      { label: "Oui, tout est immédiat", points: 3 },
      { label: "Oui, mais peu pratique", points: 2 },
      { label: "C'est compliqué", points: 1 },
      { label: "Impossible", points: 0 },
    ],
  },
  {
    q: "Votre présence en ligne reflète-t-elle la qualité de votre commerce en vrai ?",
    options: [
      { label: "Parfaitement", points: 3 },
      { label: "En partie", points: 2 },
      { label: "Pas vraiment", points: 1 },
      { label: "Pas du tout", points: 0 },
    ],
  },
];

const secteurOptions = SECTEURS.filter((s) => s.id !== "tous");

const verdicts = [
  {
    min: 13,
    max: 18,
    titre: "Votre présence en ligne est solide.",
    text: "Belle base ! Il reste sûrement quelques détails à peaufiner pour transformer encore plus de visiteurs en clients. Un aperçu ML vous montrerait le niveau au-dessus.",
  },
  {
    min: 7,
    max: 12,
    titre: "Votre commerce perd des clients sans le savoir.",
    text: "Des gens vous cherchent, mais votre présence en ligne ne les convainc pas encore — ou ne les trouve pas. La bonne nouvelle : ça se corrige vite.",
  },
  {
    min: 0,
    max: 6,
    titre: "Vous êtes quasi invisible en ligne.",
    text: "Aujourd'hui, la plupart de vos clients potentiels ne peuvent pas vous trouver ou ne sont pas rassurés. C'est exactement là que ML fait la plus grosse différence.",
  },
];

const TOTAL = questions.length; // 7 questions notées

export default function Audit() {
  const [step, setStep] = useState(0); // 0..6 questions, 7 = secteur, 8 = résultat
  const [answers, setAnswers] = useState(Array(TOTAL).fill(null));
  const [secteur, setSecteur] = useState("");
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const choose = (points) => {
    const next = [...answers];
    next[step] = points;
    setAnswers(next);
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const reset = () => {
    setAnswers(Array(TOTAL).fill(null));
    setSecteur("");
    setStep(0);
    setEmail("");
    setEmailSent(false);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim() || !isSupabaseReady) return;
    setEmailLoading(true);
    await supabase.from("leads").insert({
      source: "audit",
      email: email.trim().toLowerCase(),
      secteur: secteurLabel || null,
      score,
      verdict: verdict.titre,
    });
    setEmailLoading(false);
    setEmailSent(true);
  };

  const score = answers.reduce((sum, p) => sum + (p || 0), 0);
  const verdict = verdicts.find((v) => score >= v.min && score <= v.max) || verdicts[1];
  const secteurLabel = secteurOptions.find((s) => s.id === secteur)?.label;

  const progress = Math.min(step, TOTAL + 1) / (TOTAL + 1);
  const isResult = step > TOTAL; // step === 8

  return (
    <>
      <Seo title="Audit express de votre présence en ligne" description="Évaluez gratuitement la visibilité de votre commerce sur le web en 2 minutes et recevez des pistes concrètes d'amélioration." />
      <Navigation />
      <main data-testid="audit-page" className="relative z-10 min-h-screen px-5 sm:px-8 lg:px-12 pt-36 sm:pt-44 pb-28">
        <div className="max-w-3xl mx-auto">
          {!isResult && (
            <>
              <div className="text-center mb-12">
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6 block">
                  Audit express — 2 minutes
                </span>
                <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tighter">
                  Votre commerce est-il vraiment <span className="text-outline">visible</span> en ligne ?
                </h1>
              </div>

              {/* Progress */}
              <div className="h-1 w-full bg-white/[0.08] rounded-full overflow-hidden mb-10">
                <motion.div
                  className="h-full bg-[var(--accent)]"
                  animate={{ width: `${progress * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35 }}
                  className="bento p-7 sm:p-10"
                >
                  {step < TOTAL ? (
                    <>
                      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--accent)] mb-5">
                        Question {step + 1} / {TOTAL}
                      </div>
                      <h2 className="font-display text-2xl sm:text-3xl tracking-tight leading-snug mb-8">
                        {questions[step].q}
                      </h2>
                      <div className="space-y-3">
                        {questions[step].options.map((o) => (
                          <button
                            key={o.label}
                            data-testid={`audit-option-${o.points}`}
                            onClick={() => choose(o.points)}
                            className="w-full text-left px-5 py-4 rounded-xl border border-white/[0.1] bg-[var(--bg-soft)] hover:border-[var(--accent)] hover:bg-[#0d0d0d] transition-all duration-300 flex items-center justify-between group"
                          >
                            <span className="text-zinc-200">{o.label}</span>
                            <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-[var(--accent)] transition-colors" />
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--accent)] mb-5">
                        Dernière étape
                      </div>
                      <h2 className="font-display text-2xl sm:text-3xl tracking-tight leading-snug mb-8">
                        Quel est votre secteur ?
                      </h2>
                      <select
                        className="field bg-transparent mb-8"
                        data-testid="audit-secteur"
                        value={secteur}
                        onChange={(e) => setSecteur(e.target.value)}
                      >
                        <option value="" className="bg-black">— Sélectionnez —</option>
                        {secteurOptions.map((s) => (
                          <option key={s.id} value={s.id} className="bg-black">
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <button
                        data-testid="audit-see-result"
                        onClick={() => setStep((s) => s + 1)}
                        className="btn-pill btn-pill-accent w-full justify-center"
                      >
                        Voir mon résultat
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>

              {step > 0 && (
                <button
                  onClick={back}
                  className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Précédent
                </button>
              )}
            </>
          )}

          {/* RESULT */}
          {isResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="audit-result"
              className="text-center"
            >
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6 block">
                Votre résultat{secteurLabel ? ` — ${secteurLabel}` : ""}
              </span>

              <div className="relative inline-flex items-center justify-center mb-8">
                <div className="font-display text-8xl sm:text-9xl tracking-tighter text-[var(--accent)]">
                  {score}
                </div>
                <span className="font-display text-3xl text-zinc-600 ml-2">/18</span>
              </div>

              <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tighter mb-6">
                {verdict.titre}
              </h1>
              <p className="max-w-xl mx-auto text-zinc-400 text-lg leading-relaxed mb-10">
                {verdict.text}
              </p>

              {isSupabaseReady && (
                emailSent ? (
                  <div className="max-w-md mx-auto mb-10 bento p-5 flex items-center justify-center gap-3 text-sm text-zinc-300">
                    <Check className="w-5 h-5 text-[var(--accent)]" /> Résultat enregistré — on vous envoie vos conseils.
                  </div>
                ) : (
                  <form onSubmit={submitEmail} className="max-w-md mx-auto mb-10 bento p-6 text-left">
                    <p className="text-sm text-zinc-400 mb-4">
                      Recevez votre résultat + 2-3 conseils personnalisés selon votre score :
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        className="field flex-1"
                        placeholder="vous@commerce.fr"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="audit-email"
                      />
                      <button className="btn-pill btn-pill-accent" disabled={emailLoading} data-testid="audit-email-submit">
                        <Mail className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-2">Optionnel. Aucune sollicitation commerciale.</p>
                  </form>
                )
              )}

              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/mockup-gratuit" data-testid="audit-cta-mockup" className="btn-pill btn-pill-accent">
                  Recevoir un aperçu gratuit de mon site
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <button onClick={reset} className="btn-pill">
                  <RefreshCw className="w-4 h-4" /> Refaire le test
                </button>
              </div>

              <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Aucune sollicitation commerciale • Juste un état des lieux honnête
              </p>
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
